import { useState, useEffect, useRef, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import FrontLayout from '../../components/layouts/FrontLayout';
import TitledPanel from 'src/components/ui/TitledPanel';
import Button from '../../components/ui/Button';
import OtpField from '../../components/ui/OtpField';
import { apiPost } from 'src/lib/api/axios';
import { auth } from '@/features/auth/firebase';
import { WarningIcon } from '@phosphor-icons/react';

const CODE_LENGTH = 6;

interface MfaVerificationProps {
  onVerified: () => void;
}

export default function MfaVerification({ onVerified }: MfaVerificationProps) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef('');
  const codeInputId = useId();
  const codeDescriptionId = `${codeInputId}-description`;

  const handleCodeChange = (value: string) => {
    codeRef.current = value;
    setCode(value);
  };

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const timer = setTimeout(() => {
      setCooldownSeconds(cooldownSeconds - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  // Auto-focus the input when code is sent
  useEffect(() => {
    if (codeSent && inputRef.current) {
      inputRef.current.focus();
    }
  }, [codeSent]);

  const handleRequestCode = async () => {
    setIsRequesting(true);
    setError('');

    try {
      await apiPost('auth/mfa/request-code', null, {
        meta: { globalError: false, context: 'mfa-request' }
      });
      setCodeSent(true);
      setCooldownSeconds(60);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Failed to send verification code. Please try again.';
      setError(detail);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();

    const submittedCode = codeRef.current;
    if (submittedCode.length !== CODE_LENGTH) {
      setError(`Please enter the ${CODE_LENGTH}-digit code.`);
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      await apiPost(
        'auth/mfa/verify',
        { code: submittedCode },
        {
          meta: { globalError: false, context: 'mfa-verify' }
        }
      );

      // Force refresh the Firebase token to pick up the new mfa_verified_at claim
      await auth.currentUser?.getIdToken(true);

      onVerified();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Verification failed. Please try again.';
      setError(detail);
      codeRef.current = '';
      setCode('');
      inputRef.current?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleLogout = async () => {
    await logout({ silent: true });
    navigate('/auth/login');
  };

  return (
    <FrontLayout showTrustedBy={false}>
      <TitledPanel title="Admin Identity Verification">
        <p className="text-sm text-content-subtle">
          For security, admin accounts require an additional verification step. We'll send a code to your email address.
        </p>
        <div className="flex w-full flex-col items-center gap-6">
          {!codeSent ? (
            // Step 1: Request code
            <div className="flex w-[17.5rem] max-w-full flex-col gap-3">
              <Button
                onClick={handleRequestCode}
                disabled={isRequesting}
                variant="primary"
                fullWidth
                loading={isRequesting}
              >
                {isRequesting ? 'Sending...' : 'Send verification code'}
              </Button>
              <Button onClick={handleLogout} variant="outline" fullWidth>
                Back to homepage
              </Button>
            </div>
          ) : (
            // Step 2: Enter code
            <form onSubmit={handleVerify} className="flex w-[17.5rem] max-w-full flex-col gap-4">
              <div>
                <label htmlFor={codeInputId} className="block text-sm font-medium text-content-default mb-1">
                  Verification code
                </label>
                <OtpField
                  ref={inputRef}
                  id={codeInputId}
                  length={CODE_LENGTH}
                  value={code}
                  onValueChange={handleCodeChange}
                  disabled={isVerifying}
                  autoSubmit
                  invalid={Boolean(error)}
                  aria-describedby={codeDescriptionId}
                />
                <p id={codeDescriptionId} className="text-xs text-content-subtle mt-2">
                  Check your email for the verification code. It expires in 10 minutes.
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-misc-error text-sm">
                  <WarningIcon size={20} className="flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button type="submit" variant="primary" fullWidth disabled={isVerifying || code.length !== CODE_LENGTH}>
                {isVerifying ? 'Verifying...' : 'Verify'}
              </Button>

              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  onClick={handleRequestCode}
                  disabled={isRequesting || cooldownSeconds > 0}
                  variant="outline"
                  fullWidth
                  loading={isRequesting}
                >
                  {isRequesting
                    ? 'Sending...'
                    : cooldownSeconds > 0
                      ? `Resend code (${cooldownSeconds}s)`
                      : 'Resend code'}
                </Button>
                <Button type="button" onClick={handleLogout} variant="ghost" fullWidth>
                  Back to homepage
                </Button>
              </div>
            </form>
          )}

          {!codeSent && error && (
            <div className="flex items-center gap-2 text-misc-error text-sm">
              <WarningIcon size={20} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </TitledPanel>
    </FrontLayout>
  );
}
